import { randomUuid } from '../chunk-WVLHT3DL.js';
import { AUTHORED_REASON_ATTR, CONTESTED_ATTR, AUTHORED_BY_ATTR, AUTHORED_AT_ATTR, AUTHORED_SCOPE_ATTR } from '../chunk-AEYXO6NC.js';
import { __export } from '../chunk-PZ5AY32C.js';

// src/graph-overlay/apply.ts
var PATCHABLE_NODE_FIELDS = /* @__PURE__ */ new Set([
  "label",
  "sublabel",
  "badge",
  "kind",
  "entity_id",
  "icon",
  "attributes"
]);
var PATCHABLE_EDGE_FIELDS = /* @__PURE__ */ new Set(["label", "kind", "attributes"]);
function recordClaims(claims, elementId, patch, scope, by) {
  let perField = claims.get(elementId);
  if (!perField) {
    perField = /* @__PURE__ */ new Map();
    claims.set(elementId, perField);
  }
  for (const [field, value] of Object.entries(patch)) {
    const list = perField.get(field);
    if (list) list.push({ field, value, by, scope });
    else perField.set(field, [{ field, value, by, scope }]);
  }
}
function contestedClaims(perField, allowed) {
  if (!perField) return [];
  const out = [];
  for (const [field, entries] of perField) {
    if (!allowed.has(field) || entries.length < 2) continue;
    const byScope = /* @__PURE__ */ new Map();
    for (const claim of entries) {
      const list = byScope.get(claim.scope);
      if (list) list.push(claim);
      else byScope.set(claim.scope, [claim]);
    }
    for (const rivals of byScope.values()) {
      const distinct = new Set(rivals.map((c) => claimKey(c.value)));
      if (distinct.size < 2) continue;
      out.push(...rivals);
    }
  }
  return out;
}
function claimKey(value) {
  try {
    return JSON.stringify(value ?? null);
  } catch {
    return String(value);
  }
}
function applyPatch(element, patch, allowed, contested = []) {
  let changed = false;
  const next = { ...element };
  for (const [key, value] of Object.entries(patch)) {
    if (!allowed.has(key)) continue;
    if (key === "attributes" && value && typeof value === "object") {
      next.attributes = { ...element.attributes, ...value };
    } else {
      next[key] = value;
    }
    changed = true;
  }
  if (contested.length > 0) {
    const base = next.attributes ?? element.attributes;
    next.attributes = { ...base, [CONTESTED_ATTR]: contested };
    changed = true;
  }
  return changed ? next : element;
}
function provenanceOf(body) {
  const out = {};
  if (body.authored_by) out[AUTHORED_BY_ATTR] = body.authored_by;
  if (body.authored_at) out[AUTHORED_AT_ATTR] = body.authored_at;
  if (body.authored_reason) out[AUTHORED_REASON_ATTR] = body.authored_reason;
  if (body.authored_scope) out[AUTHORED_SCOPE_ATTR] = body.authored_scope;
  return out;
}
function authored(element, provenance) {
  return {
    ...element,
    origin: "user",
    ..."virtual" in element ? { virtual: false } : {},
    attributes: { ...provenance, ...element.attributes ?? {} }
  };
}
function applySceneOverlay(scene, overlays) {
  const bodies = overlays.filter((o) => o != null && !isNoop(o));
  if (bodies.length === 0) return scene;
  const nodes = new Map(scene.nodes.map((n) => [n.id, n]));
  const edges = new Map(scene.edges.map((e) => [e.id, e]));
  const groups = new Map(scene.groups.map((g) => [g.id, g]));
  const hiddenNodes = /* @__PURE__ */ new Set();
  const hiddenEdges = /* @__PURE__ */ new Set();
  const nodePatches = /* @__PURE__ */ new Map();
  const edgePatches = /* @__PURE__ */ new Map();
  const positions = /* @__PURE__ */ new Map();
  const nodeClaims = /* @__PURE__ */ new Map();
  const edgeClaims = /* @__PURE__ */ new Map();
  for (const body of bodies) {
    const provenance = provenanceOf(body);
    for (const node of body.nodes) nodes.set(node.id, authored(node, provenance));
    for (const edge of body.edges) edges.set(edge.id, authored(edge, provenance));
    for (const [id, point] of Object.entries(body.positions ?? {})) {
      positions.set(id, point);
    }
    for (const group of body.groups) groups.set(group.id, group);
    for (const id of body.hidden_node_ids) hiddenNodes.add(id);
    for (const id of body.hidden_edge_ids) hiddenEdges.add(id);
    const scope = String(body.authored_scope ?? "");
    const by = String(body.authored_by ?? "");
    for (const [id, patch] of Object.entries(body.node_patches)) {
      nodePatches.set(id, { ...nodePatches.get(id), ...patch });
      recordClaims(nodeClaims, id, patch, scope, by);
    }
    for (const [id, patch] of Object.entries(body.edge_patches)) {
      edgePatches.set(id, { ...edgePatches.get(id), ...patch });
      recordClaims(edgeClaims, id, patch, scope, by);
    }
  }
  for (const [id, patch] of nodePatches) {
    const node = nodes.get(id);
    if (!node) continue;
    const contested = contestedClaims(nodeClaims.get(id), PATCHABLE_NODE_FIELDS);
    nodes.set(id, applyPatch(node, patch, PATCHABLE_NODE_FIELDS, contested));
  }
  for (const [id, patch] of edgePatches) {
    const edge = edges.get(id);
    if (!edge) continue;
    const contested = contestedClaims(edgeClaims.get(id), PATCHABLE_EDGE_FIELDS);
    edges.set(id, applyPatch(edge, patch, PATCHABLE_EDGE_FIELDS, contested));
  }
  for (const [id, point] of positions) {
    const node = nodes.get(id);
    if (!node || node.origin !== "user") continue;
    nodes.set(id, { ...node, attributes: { ...node.attributes, position: point } });
  }
  for (const id of hiddenNodes) nodes.delete(id);
  for (const id of hiddenEdges) edges.delete(id);
  const liveNodes = new Set(nodes.keys());
  const keptEdges = [...edges.values()].filter(
    (edge) => liveNodes.has(edge.source) && liveNodes.has(edge.target)
  );
  const nodeList = [...nodes.values()];
  const groupList = [];
  for (const group of groups.values()) {
    const members = group.member_node_ids.filter((id) => liveNodes.has(id));
    if (members.length === 0 && group.member_node_ids.length > 0) continue;
    groupList.push(
      members.length === group.member_node_ids.length ? group : { ...group, member_node_ids: members }
    );
  }
  const chainIds = [
    ...new Set(nodeList.filter((n) => n.chain_id && !n.off_chain).map((n) => n.chain_id))
  ].sort();
  return {
    ...scene,
    nodes: nodeList,
    edges: keptEdges,
    groups: groupList,
    chain_ids: chainIds,
    multi_chain: chainIds.length > 1
  };
}
function isNoop(overlay) {
  return overlay.nodes.length === 0 && overlay.edges.length === 0 && overlay.groups.length === 0 && overlay.hidden_node_ids.length === 0 && overlay.hidden_edge_ids.length === 0 && Object.keys(overlay.node_patches).length === 0 && Object.keys(overlay.edge_patches).length === 0 && Object.keys(overlay.positions ?? {}).length === 0;
}
function hiddenCount(overlay) {
  if (!overlay) return 0;
  return overlay.hidden_node_ids.length + overlay.hidden_edge_ids.length;
}
function hiddenRefs(overlay) {
  if (!overlay) return [];
  return [
    ...overlay.hidden_node_ids.map((id) => ({ kind: "node", id })),
    ...overlay.hidden_edge_ids.map((id) => ({ kind: "edge", id }))
  ];
}

// src/graph-overlay/drafts.ts
var AUTHORED_ID_PREFIX = "authored:";
function isAuthoredId(id) {
  return id.startsWith(AUTHORED_ID_PREFIX);
}
function authoredId(kind) {
  return `${AUTHORED_ID_PREFIX}${kind}:${randomUuid()}`;
}
function draftNode(draft) {
  const chainId = draft.chainId ?? null;
  return {
    id: authoredId("node"),
    kind: draft.kind ?? "entity",
    label: draft.label,
    sublabel: draft.sublabel ?? null,
    chain_id: chainId,
    address: draft.address ?? null,
    group_id: null,
    // An authored node sits off-chain unless it was pinned to a chain: an
    // analyst's "suspected mule" is not an observed on-chain account, and
    // letting it into the chain roll-up would overstate the graph's reach.
    off_chain: chainId == null,
    entity_id: null,
    icon: null,
    assets: [],
    value: null,
    badge: null,
    attributes: {},
    source_refs: [],
    // Not 1: an assertion is not evidence. The renderer already dims by
    // confidence, so an authored node reads as softer than a decoded one even
    // before its badge is noticed.
    confidence: 0.5,
    detail_tier: "standard",
    origin: "user",
    facts: []
  };
}
function draftEdge(draft) {
  return {
    id: authoredId("edge"),
    source: draft.source,
    target: draft.target,
    // `flow`, not `transfer`: an authored link asserts a relationship, and
    // calling it a transfer would imply a value movement nobody observed.
    kind: draft.kind ?? "flow",
    label: draft.label ?? null,
    asset: null,
    direction: null,
    from_chain: null,
    to_chain: null,
    cross_chain: false,
    virtual: false,
    icon: null,
    attributes: {},
    detail_tier: "standard",
    origin: "user",
    facts: []
  };
}

// src/graph-overlay/edit.ts
var edit_exports = {};
__export(edit_exports, {
  addEdge: () => addEdge,
  addNode: () => addNode,
  explain: () => explain,
  hide: () => hide,
  isPatched: () => isPatched,
  moveNode: () => moveNode,
  reasonOf: () => reasonOf,
  relabel: () => relabel,
  remove: () => remove,
  restoreHidden: () => restoreHidden,
  revert: () => revert,
  unhide: () => unhide
});
function addNode(overlay, node) {
  return { ...overlay, nodes: [...overlay.nodes.filter((n) => n.id !== node.id), node] };
}
function addEdge(overlay, edge) {
  return { ...overlay, edges: [...overlay.edges.filter((e) => e.id !== edge.id), edge] };
}
function relabel(overlay, target, label) {
  const { kind, id } = target;
  if (kind === "node") {
    const authored3 = overlay.nodes.find((n) => n.id === id);
    if (authored3) {
      return { ...overlay, nodes: overlay.nodes.map((n) => n.id === id ? { ...n, label } : n) };
    }
    return {
      ...overlay,
      node_patches: { ...overlay.node_patches, [id]: { ...overlay.node_patches[id], label } }
    };
  }
  const authored2 = overlay.edges.find((e) => e.id === id);
  if (authored2) {
    return { ...overlay, edges: overlay.edges.map((e) => e.id === id ? { ...e, label } : e) };
  }
  return {
    ...overlay,
    edge_patches: { ...overlay.edge_patches, [id]: { ...overlay.edge_patches[id], label } }
  };
}
function explain(overlay, target, reason) {
  const { kind, id } = target;
  const text = reason.trim();
  const withReason = (attributes) => {
    const next = { ...attributes ?? {} };
    if (text) next[AUTHORED_REASON_ATTR] = text;
    else delete next[AUTHORED_REASON_ATTR];
    return next;
  };
  if (kind === "node") {
    const authored3 = overlay.nodes.find((n) => n.id === id);
    if (authored3) {
      return {
        ...overlay,
        nodes: overlay.nodes.map(
          (n) => n.id === id ? { ...n, attributes: withReason(n.attributes) } : n
        )
      };
    }
    const patch2 = overlay.node_patches[id] ?? {};
    return {
      ...overlay,
      node_patches: {
        ...overlay.node_patches,
        [id]: {
          ...patch2,
          attributes: withReason(patch2.attributes)
        }
      }
    };
  }
  const authored2 = overlay.edges.find((e) => e.id === id);
  if (authored2) {
    return {
      ...overlay,
      edges: overlay.edges.map(
        (e) => e.id === id ? { ...e, attributes: withReason(e.attributes) } : e
      )
    };
  }
  const patch = overlay.edge_patches[id] ?? {};
  return {
    ...overlay,
    edge_patches: {
      ...overlay.edge_patches,
      [id]: {
        ...patch,
        attributes: withReason(patch.attributes)
      }
    }
  };
}
function revert(overlay, target, fields) {
  const { kind, id } = target;
  const key = kind === "node" ? "node_patches" : "edge_patches";
  const patch = overlay[key][id];
  if (!patch) return overlay;
  const next = { ...overlay[key] };
  if (fields && fields.length > 0) {
    const kept = Object.fromEntries(
      Object.entries(patch).filter(([field]) => !fields.includes(field))
    );
    if (Object.keys(kept).length === Object.keys(patch).length) return overlay;
    if (Object.keys(kept).length > 0) next[id] = kept;
    else delete next[id];
  } else {
    delete next[id];
  }
  return { ...overlay, [key]: next };
}
function reasonOf(overlay, target) {
  const { kind, id } = target;
  const authored2 = kind === "node" ? overlay.nodes.find((n) => n.id === id) : overlay.edges.find((e) => e.id === id);
  const attributes = authored2?.attributes ?? (kind === "node" ? overlay.node_patches : overlay.edge_patches)[id]?.attributes;
  const reason = attributes?.[AUTHORED_REASON_ATTR];
  return typeof reason === "string" && reason.trim() ? reason : void 0;
}
function isPatched(overlay, target, field) {
  const patch = (target.kind === "node" ? overlay.node_patches : overlay.edge_patches)[target.id];
  if (!patch) return false;
  return field ? field in patch : Object.keys(patch).length > 0;
}
function moveNode(overlay, nodeId, position) {
  if (!isAuthoredId(nodeId)) return overlay;
  const current = overlay.positions[nodeId];
  if (current && current.x === position.x && current.y === position.y) return overlay;
  return { ...overlay, positions: { ...overlay.positions, [nodeId]: position } };
}
function hide(overlay, target) {
  const key = target.kind === "node" ? "hidden_node_ids" : "hidden_edge_ids";
  if (overlay[key].includes(target.id)) return overlay;
  return { ...overlay, [key]: [...overlay[key], target.id] };
}
function unhide(overlay, target) {
  const key = target.kind === "node" ? "hidden_node_ids" : "hidden_edge_ids";
  if (!overlay[key].includes(target.id)) return overlay;
  return { ...overlay, [key]: overlay[key].filter((id) => id !== target.id) };
}
function restoreHidden(overlay) {
  if (overlay.hidden_node_ids.length === 0 && overlay.hidden_edge_ids.length === 0) return overlay;
  return { ...overlay, hidden_node_ids: [], hidden_edge_ids: [] };
}
function remove(overlay, target) {
  if (!isAuthoredId(target.id)) return overlay;
  if (target.kind === "edge") {
    return { ...overlay, edges: overlay.edges.filter((e) => e.id !== target.id) };
  }
  const { [target.id]: _dropped, ...positions } = overlay.positions;
  return {
    ...overlay,
    nodes: overlay.nodes.filter((n) => n.id !== target.id),
    edges: overlay.edges.filter((e) => e.source !== target.id && e.target !== target.id),
    positions
  };
}

export { AUTHORED_ID_PREFIX, applySceneOverlay, authoredId, draftEdge, draftNode, hiddenCount, hiddenRefs, isAuthoredId, isNoop, edit_exports as overlayEdit };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map